<script>
  import { _ } from '@sveltia/i18n';
  import { Button, EmptyState } from '@sveltia/ui';
  import { untrack } from 'svelte';

  import EntryEditor from '$lib/components/contents/details/editor/entry-editor.svelte';
  import EntryPreview from '$lib/components/contents/details/preview/entry-preview.svelte';
  import { isDraftReadonly } from '$lib/services/config/readonly';
  import { getEntryDraftContext } from '$lib/services/contents/draft/state.svelte';
  import { toggleLocale } from '$lib/services/contents/draft/update/locale';
  import { createScrollSync } from '$lib/services/contents/editor/scroll-sync';
  import { entryEditorSettings } from '$lib/services/contents/editor/settings';
  import { getLocaleLabel } from '$lib/services/contents/i18n';
  import { isPendingDeletion } from '$lib/services/workflow';

  /**
   * @import { EntryEditorPane } from '$lib/types/private';
   */

  /**
   * @typedef {object} Props
   * @property {string} id The wrapper element’s `id` attribute.
   * @property {{ current: ?EntryEditorPane }} thisPane This pane’s mode and locale.
   * @property {HTMLElement} [thisPaneContentArea] This pane’s content area, bound for the parent.
   * @property {HTMLElement} [thatPaneContentArea] Another pane’s content area.
   */

  const entryDraft = getEntryDraftContext();

  /** @type {Props} */
  let {
    /* eslint-disable prefer-const */
    id,
    thisPane,
    thisPaneContentArea = $bindable(),
    thatPaneContentArea = undefined,
    /* eslint-enable prefer-const */
  } = $props();

  const { syncScrolling } = $derived(entryEditorSettings.current ?? {});
  const locale = $derived(thisPane.current?.locale);
  const mode = $derived(thisPane.current?.mode);
  const hasContent = $derived(!!locale && !!entryDraft.current?.currentValues[locale]);
  // A read-only entry, or one awaiting deletion, is shown for reference only, so its locales can’t
  // be enabled
  const locked = $derived(
    isDraftReadonly(entryDraft.current) || isPendingDeletion(entryDraft.current?.originalEntry),
  );
  /* v8 ignore start -- only read for a disabled locale, which the pane always has */
  const labelOptions = $derived({
    values: { locale: locale ? (getLocaleLabel(locale) ?? locale) : '' },
  });
  /* v8 ignore stop */

  /** @type {HTMLElement | undefined} */
  let contentArea = $state();

  /* eslint-disable jsdoc/require-jsdoc */
  const scrollSync = createScrollSync({
    get syncScrolling() {
      return syncScrolling;
    },
    get mode() {
      return mode;
    },
    get contentArea() {
      return contentArea;
    },
    get thisPaneContentArea() {
      return thisPaneContentArea;
    },
    set thisPaneContentArea(element) {
      thisPaneContentArea = element;
    },
    get thatPaneContentArea() {
      return thatPaneContentArea;
    },
  });
  /* eslint-enable jsdoc/require-jsdoc */

  $effect(() => {
    // Initialize the scroll synchronization when the content area is ready. The pane mode is also a
    // dependency because the edit mode always uses the main content area, while the preview mode
    // uses an iframe if a custom preview stylesheet or template applies. The iframe is only there
    // once it has loaded, so it initializes the sync again then.
    void [thisPane.current?.mode, contentArea];
    // The initialization writes `thisPaneContentArea`, which it also reads, so it’s left out of
    // the dependencies to keep the effect from running again on its own account
    untrack(() => scrollSync.initialize());
  });

  // Forget this pane once it’s gone, so the module-level reference doesn’t keep the DOM of a closed
  // editor from being garbage-collected
  $effect(() => () => {
    scrollSync.destroy();
  });
</script>

<div role="none" {id} class="wrapper">
  {#if locale && entryDraft.current?.currentLocales[locale]}
    <div role="none" class="content" bind:this={contentArea} onfocusin={scrollSync.onFocusIn}>
      {#if mode === 'preview'}
        <EntryPreview
          {locale}
          onFrameLoad={(iframe) => {
            scrollSync.setPreviewFrame(iframe);
            scrollSync.initialize();
          }}
        />
      {:else}
        <EntryEditor {locale} />
      {/if}
    </div>
  {:else if mode === 'edit'}
    <EmptyState>
      <span role="alert">
        {_(hasContent ? 'locale_x_now_disabled' : 'locale_x_has_been_disabled', labelOptions)}
      </span>
      {#if !locked}
        <Button
          variant="tertiary"
          label={_(hasContent ? 'reenable_x_locale' : 'enable_x_locale', labelOptions)}
          onclick={() => {
            /* v8 ignore next 3 -- the button is only offered for a disabled locale */
            if (locale && entryDraft.current) {
              toggleLocale({ draft: entryDraft.current, locale });
            }
          }}
        />
      {/if}
    </EmptyState>
  {/if}
</div>

<style>
  .wrapper {
    display: contents;
  }

  .content {
    --field-editor-padding: 16px;
    flex: auto;
    overflow-y: auto;
    scroll-behavior: auto; /* Don’t use smooth scroll for syncing */
    overscroll-behavior-y: contain;

    @media (width < 768px) {
      --field-editor-padding: 12px;
    }
  }
</style>
